import { Suspense, useEffect, useState } from 'react';
import type { ApiClient } from '../api/client';
import type {
  App,
  DefinitionProcess,
  Draft,
  DraftReview,
  JsonObject,
  Problem,
  Proposal,
  ReviewFinding,
  StudioTrial,
} from '../api/types';
import { BpmnViewerLazy } from '../bpmn/BpmnViewerLazy';
import { parseBpmnDefinitions } from '../bpmn/model';
import type { BpmnDefinitionsModel } from '../bpmn/types';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Tabs } from '../components/Tabs';
import { DmnViewerLazy } from '../dmn/DmnViewerLazy';
import { DraftEditor } from '../pages/DraftEditor';
import { ReviewPanel, type FindingTarget } from '../pages/ReviewPanel';
import { TryButton } from '../pages/TryButton';
import { RunStrip } from '../pages/RunStrip';
import { usePlatform } from '../app/platform';
import { Markdown } from '../lib/markdown';
import { STUDIO_VIEWS, type Route, type StudioRoute, type StudioView } from '../app/router';
import { appHeadChipView } from '../app/status';
import { useAsync } from '../app/useAsync';
import { elapsedSeconds, useNow } from '../app/useNow';
import { Inspector, type InspectorElement } from './Inspector';

export interface CanvasProps {
  workspaceId: string;
  route: StudioRoute;
  /** Draft を持つ microapp。 */
  apps: App[];
  /** 選んでいる microapp の Draft（head）。 */
  draft: Draft | null;
  /** 選んでいる microapp の未決定の変更案。 */
  proposal: Proposal | null;
  /** AI のターンが queued / running。 */
  running: boolean;
  /** 実行中のターンが始まった時刻（`startedAt` か `createdAt`）。止まっていれば null（U5）。 */
  runningSince?: string | null;
  navigate(route: Route, options?: { replace?: boolean }): void;
  onNewMicroapp(): void;
  /** インスペクタの「この要素を対象にする」。 */
  onTarget(elementId: string): void;
  /** コード表示の保存・削除の応答の Draft。 */
  onDraftChange(draft: Draft): void;
  onReloadDraft(): void;
  /** 選んでいる microapp の head のレビュー（GET A/draft/review）。まだ読んでいなければ null。 */
  review: DraftReview | null;
  validating: boolean;
  /** 検証の失敗。レビューの取得の失敗もここで受ける（review が null のときに出す）。 */
  validateProblem: Problem | null;
  onValidate(): void;
  /** レビューの「AI に直させる」（D30）。 */
  onAskFix(finding: ReviewFinding): void;
  /** 帯に出している試運転（useDraftTrial）。無ければ帯を出さない。 */
  trial: StudioTrial | null;
  /** 配備の受付中（「試す」を「配備中…」にする）。 */
  trialDeploying: boolean;
  /** 試運転の開始の受付中（帯の「開始中…」。「試す」とは分ける。A15）。 */
  trialStarting: boolean;
  /** 配備・開始の失敗。 */
  trialProblem: Problem | null;
  onTry(confirmReplace: boolean): void;
  onStartTrial(processKey: string, input: JsonObject): void;
  onDismissTrial(): void;
  /** 帯の「再取得」（読み直しが失敗したとき。A4）。 */
  onReloadTrial(): void;
  onPublish(): void;
  /** 配備後の getDefinition のプロセス。配備前は空。 */
  processes: DefinitionProcess[];
  /** getDefinition の読み込み中（読み込み中を「取得できなかった」と出さない。A11）。 */
  processesLoading: boolean;
  /** run strip の「AI に原因を聞く」。レビューの指摘（onAskFix）とは文言が違うので別の口にする。 */
  onAskTrialFix(text: string): void;
}

const VIEW_LABELS: Record<StudioView, string> = {
  memo: '整理メモ',
  process: '業務フロー',
  decisions: '判断ルール',
  code: 'コード',
  review: 'レビュー',
};

type FileView = Exclude<StudioView, 'code' | 'review'>;

const EMPTY_TITLES: Record<FileView, string> = {
  memo: '整理メモはまだありません',
  process: '業務フローはまだありません',
  decisions: '判断ルールはまだありません',
};

/**
 * Studio の成果物（Claude Design の stage）。成果物のタブ・表示のタブ・版の切り替えと、表示ごとの中身。
 * 変更案があれば既定で変更案の版（staging）を読む。反映するまで head は変わらない（C6）。
 */
export function Canvas(props: CanvasProps) {
  const { workspaceId, route, apps, draft, proposal, running, navigate } = props;
  const [version, setVersion] = useState<'proposal' | 'head'>('proposal');
  // 変更案が入れ替わったら（別の変更案・決定して消えた）「表示する版」を変更案に戻す。
  const [shownProposal, setShownProposal] = useState<string | null>(proposal?.id ?? null);
  if (shownProposal !== (proposal?.id ?? null)) {
    setShownProposal(proposal?.id ?? null);
    setVersion('proposal');
  }

  if (apps.length === 0) {
    return (
      <div className="studio-canvas">
        <EmptyState
          title="まだ microapp がありません"
          description="AI に作りたい業務を伝えるか、ここから新しい microapp を作ります。"
          action={
            <Button variant="primary" onClick={props.onNewMicroapp}>
              新しい microapp
            </Button>
          }
        />
      </div>
    );
  }

  const appId = route.appId ?? apps[0]?.id ?? null;
  const view: StudioView = route.view ?? 'memo';
  const select = (next: Partial<StudioRoute>) => navigate({ ...route, ...next }, { replace: true });
  const revisionId = proposal !== null && version === 'proposal' ? proposal.revisionId : null;
  const selectedApp = apps.find((app) => app.id === appId);
  // 表示のタブの件数は、表示するレポート（現在の版のもの、無ければ前回）の指摘の数（Claude Design の view tabs）。
  const shownReport = props.review?.report ?? props.review?.latestReport ?? null;
  const chip = appHeadChipView(props.review);
  const reviewCount = shownReport === null ? 0 : shownReport.errorCount + shownReport.warningCount;
  const reviewLabel = (
    <>
      {VIEW_LABELS.review}
      {reviewCount > 0 && (
        <>
          {' '}
          <span
            className={`studio-count ${shownReport !== null && shownReport.errorCount > 0 ? 'studio-count-error' : 'studio-count-warning'}`}
          >
            {reviewCount}
          </span>
        </>
      )}
    </>
  );

  return (
    <div className="studio-canvas">
      <div className="studio-canvas-bar">
        <Tabs
          label="成果物の切り替え"
          active={appId ?? ''}
          onSelect={(key) => navigate({ name: 'studio', workspaceId, appId: key, ...(route.view ? { view: route.view } : {}) }, { replace: true })}
          items={apps.map((app) => ({
            key: app.id,
            label: (
              <span className="studio-app-tab">
                {app.name}
                {/* 選んでいる app のレビューしか読まないので、ほかの app は「下書き」のまま（G24）。 */}
                <span className="studio-chip">{app.id === appId && chip !== null ? chip.label : '下書き'}</span>
              </span>
            ),
          }))}
        />
        <span className="spacer" />
        <Button onClick={props.onNewMicroapp}>新しい microapp</Button>
      </div>
      {draft !== null && selectedApp !== undefined && (
        <div className="studio-app-head">
          <h2 className="studio-app-name">{selectedApp.name}</h2>
          {/* appKey は作成後に変えられないので、作った後にここで確かめられるようにする（U1・B19）。 */}
          <code className="studio-app-key" title="appKey（あとから変えられません）">
            {selectedApp.appKey}
          </code>
          {chip !== null && <span className="studio-chip">{chip.label}</span>}
          <span className="spacer" />
          <div className="studio-app-actions">
            <TryButton review={props.review} onTry={props.onTry} busy={props.trialDeploying} />
            {/* 公開はツールに無い（設計 03 章 L5）。利用者の操作だけ。 */}
            <Button onClick={props.onPublish}>公開</Button>
          </div>
        </div>
      )}
      <div className="row">
        <Tabs
          label="表示の切り替え"
          active={view}
          onSelect={(key) => select({ view: key as StudioView, file: undefined, element: undefined })}
          items={STUDIO_VIEWS.map((key) => ({ key, label: key === 'review' ? reviewLabel : VIEW_LABELS[key] }))}
        />
        {proposal !== null && view !== 'code' && view !== 'review' && (
          <Tabs
            label="表示する版"
            active={version}
            onSelect={(key) => setVersion(key === 'head' ? 'head' : 'proposal')}
            items={[
              { key: 'proposal', label: '変更案' },
              { key: 'head', label: '反映済み' },
            ]}
          />
        )}
      </div>
      {running && <RunningStrip since={props.runningSince ?? null} />}
      {props.trial !== null && appId !== null ? (
        <RunStrip
          workspaceId={workspaceId}
          appId={appId}
          trial={props.trial}
          headDigest={props.review?.filesDigest ?? null}
          processes={props.processes}
          processesLoading={props.processesLoading}
          starting={props.trialStarting}
          problem={props.trialProblem}
          onStart={props.onStartTrial}
          onDismiss={props.onDismissTrial}
          onAskFix={props.onAskTrialFix}
          onReload={props.onReloadTrial}
          navigate={navigate}
        />
      ) : (
        // 帯の元になる試運転がまだ無いときの配備の失敗（422 export_invalid・409 draft_has_errors など）。
        // 帯があれば同じ problem を RunStrip の中に出すので、ここでは出さない。
        <ProblemNotice
          problem={props.trialProblem}
          tone={props.trialProblem !== null && props.trialProblem.status === 503 ? 'notice' : 'error'}
        />
      )}
      {draft === null || appId === null ? (
        <EmptyState title="この microapp には Draft がありません" />
      ) : view === 'code' ? (
        <DraftEditor
          workspaceId={workspaceId}
          appId={appId}
          draft={draft}
          file={route.file ?? null}
          onSelectFile={(file) => select({ file })}
          onDraftChange={props.onDraftChange}
          onReload={props.onReloadDraft}
        />
      ) : view === 'review' ? (
        props.review !== null ? (
          <ReviewPanel
            review={props.review}
            validating={props.validating}
            validateProblem={props.validateProblem}
            onValidate={props.onValidate}
            targetOf={(finding) => findingTarget(finding, select)}
            onAskFix={props.onAskFix}
          />
        ) : props.validateProblem !== null ? (
          <ProblemNotice problem={props.validateProblem} />
        ) : (
          <Spinner label="レビューを取得しています" />
        )
      ) : (
        <RevisionView
          // 版・表示を切り替えたら、読んだ一覧と本文を持ち越さない。
          key={`${appId}:${revisionId ?? 'head'}:${view}`}
          workspaceId={workspaceId}
          appId={appId}
          draft={draft}
          proposal={proposal}
          revisionId={revisionId}
          view={view}
          file={route.file ?? null}
          element={route.element ?? null}
          running={running}
          onSelectFile={(file) => select({ file, element: undefined })}
          onSelectElement={(element) => select({ element: element ?? undefined })}
          onTarget={props.onTarget}
        />
      )}
    </div>
  );
}

interface RevisionViewProps {
  workspaceId: string;
  appId: string;
  draft: Draft;
  proposal: Proposal | null;
  /** 読む版。null は head。 */
  revisionId: string | null;
  view: FileView;
  file: string | null;
  element: string | null;
  running: boolean;
  onSelectFile(path: string): void;
  onSelectElement(elementId: string | null): void;
  onTarget(elementId: string): void;
}

function RevisionView(props: RevisionViewProps) {
  const { api } = usePlatform();
  const { workspaceId, appId, draft, proposal, revisionId, view } = props;
  // AI の作業が終わった（running が変わった）ときと、変更案の要約が更新されたときに読み直す。
  const refresh = `${draft.headRevisionId}:${proposal?.updatedAt ?? ''}:${props.running}`;
  const paths = useAsync(async () => {
    const head = draft.files.map((entry) => entry.path);
    if (revisionId === null || proposal === null) return head;
    // S2 に版のファイル一覧の API は無い（B17）。変更案の親（base）の一覧に base→staging の追加と削除を重ねる。
    // 反映済みの一覧に重ねると、利用者の直接編集で head が base より進んだとき、その版に無いファイルが混ざる。
    const base = await basePaths(api, workspaceId, appId, draft, proposal.baseRevisionId, head);
    const diff = await api.getRevisionDiff(workspaceId, appId, proposal.baseRevisionId, { to: revisionId, view: 'file' });
    const removed = new Set(diff.files.filter((file) => file.change === 'removed').map((file) => file.path));
    const added = diff.files.filter((file) => file.change !== 'removed').map((file) => file.path);
    return [...new Set([...base, ...added])].filter((path) => !removed.has(path)).sort();
  }, [api, workspaceId, appId, revisionId, refresh]);

  const candidates = (paths.data ?? []).filter((path) => matchesView(view, path)).sort(byHighlevelFirst);
  const current = props.file !== null && candidates.includes(props.file) ? props.file : (candidates[0] ?? null);

  const content = useAsync(async () => {
    if (current === null) return null;
    const loaded = await api.getDraftFile(workspaceId, appId, current, revisionId ? { revisionId } : {});
    return new TextDecoder().decode(loaded.bytes);
  }, [api, workspaceId, appId, current, revisionId, refresh]);

  const [model, setModel] = useState<BpmnDefinitionsModel | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const xml = view === 'process' ? content.data : null;
  useEffect(() => {
    setModel(null);
    setRenderError(null);
    if (xml === null) return;
    let active = true;
    parseBpmnDefinitions(xml)
      .then((parsed) => {
        if (active) setModel(parsed);
      })
      .catch(() => {
        if (active) setModel(null);
      });
    return () => {
      active = false;
    };
  }, [xml]);

  if (paths.loading && paths.data === null) return <Spinner label="ファイルを取得しています" />;
  if (paths.problem) {
    return <ProblemNotice problem={paths.problem} action={<Button onClick={paths.reload}>再取得</Button>} />;
  }
  if (current === null) return <EmptyState title={EMPTY_TITLES[view]} />;

  return (
    <div className="stack">
      {candidates.length > 1 && (
        <Tabs
          label="ファイルの切り替え"
          active={current}
          onSelect={props.onSelectFile}
          items={candidates.map((path) => ({ key: path, label: path }))}
        />
      )}
      <ProblemNotice problem={content.problem} action={<Button onClick={content.reload}>再取得</Button>} />
      {content.data === null ? (
        content.loading && <Spinner label="ファイルの中身を取得しています" />
      ) : view === 'memo' ? (
        <section className="studio-memo" aria-label={`${current} の内容`}>
          <Markdown text={content.data} />
        </section>
      ) : view === 'process' ? (
        <div className="diagram-layout">
          <div className="diagram-area">
            {model !== null && !model.hasDiagram ? (
              <p className="hint studio-no-diagram">この業務フローには図の配置情報（BPMNDI）がありません。</p>
            ) : (
              <Suspense fallback={<Spinner label="図を読み込んでいます" />}>
                <BpmnViewerLazy
                  xml={content.data}
                  selectedElementId={props.element}
                  onSelect={props.onSelectElement}
                  onImportError={(error) => setRenderError(error.message)}
                />
              </Suspense>
            )}
            {renderError && <p className="failure">図を描画できませんでした: {renderError}</p>}
          </div>
          <Inspector
            element={findElement(model, props.element)}
            onTarget={props.onTarget}
            onClose={() => props.onSelectElement(null)}
          />
        </div>
      ) : (
        <div className="dmn-area">
          <Suspense fallback={<Spinner label="判断ルールを読み込んでいます" />}>
            <DmnViewerLazy xml={content.data} view="table" />
          </Suspense>
        </div>
      )}
    </div>
  );
}

/**
 * 変更案の親（base）のファイル一覧。版のファイル一覧の API が無いので、反映済み（head）の一覧に
 * base→head の差分を逆に当てて復元する。base が head と同じなら呼び出しを増やさない。
 */
async function basePaths(
  api: ApiClient,
  workspaceId: string,
  appId: string,
  draft: Draft,
  baseRevisionId: string,
  head: string[],
): Promise<string[]> {
  if (draft.headRevisionId === baseRevisionId) return head;
  const diff = await api.getRevisionDiff(workspaceId, appId, baseRevisionId, { to: draft.headRevisionId, view: 'file' });
  const addedInHead = new Set(diff.files.filter((file) => file.change === 'added').map((file) => file.path));
  const removedInHead = diff.files.filter((file) => file.change === 'removed').map((file) => file.path);
  return [...new Set([...head.filter((path) => !addedInHead.has(path)), ...removedInHead])];
}

function matchesView(view: FileView, path: string): boolean {
  if (view === 'memo') return path.startsWith('docs/') && path.endsWith('.md');
  if (view === 'process') return path.endsWith('.bpmn');
  return path.endsWith('.dmn');
}

/**
 * AI が更新中の帯（S3）。始まってからの経過時間を足す（U5）。
 * 1 秒ごとに変わる秒は aria-hidden にして、ライブリージョン（role="status"）の読み上げから外す。
 */
function RunningStrip({ since }: { since: string | null }) {
  const seconds = elapsedSeconds(since, useNow(true));
  return (
    <p className="studio-running-strip" role="status">
      <span>AI が更新しています</span>
      {since !== null && <span aria-hidden="true">{`（${seconds} 秒）`}</span>}
      <span> · キャンバスは途中の状態です</span>
    </p>
  );
}

/** 高レベルの業務フロー（models/highlevel/）を実行可能な BPMN より先に出す。 */
function byHighlevelFirst(left: string, right: string): number {
  const rank = (path: string) => (path.startsWith('models/highlevel/') ? 0 : 1);
  return rank(left) - rank(right) || left.localeCompare(right);
}

function findElement(model: BpmnDefinitionsModel | null, elementId: string | null): InspectorElement | null {
  if (model === null || elementId === null) return null;
  for (const process of model.processes) {
    const element = process.elements[elementId];
    if (element) return { id: element.id, name: element.name, type: element.type.replace(/^bpmn:/, '').replace(/^./, (c) => c.toLowerCase()) };
  }
  return null;
}

/**
 * 指摘の移動先。BPMN の要素は業務フロー、DMN の要素は判断ルールのそのファイルへ、要素が無ければコードのファイルへ。
 * パスが無い指摘（config・DDL の段の一部）は移動しない。
 */
function findingTarget(finding: ReviewFinding, select: (next: Partial<StudioRoute>) => void): FindingTarget | null {
  const { path, elementId } = finding;
  if (path === null) return null;
  if (elementId !== null && path.endsWith('.bpmn')) {
    return { label: '要素へ', onOpen: () => select({ view: 'process', file: path, element: elementId }) };
  }
  if (elementId !== null && path.endsWith('.dmn')) {
    return { label: '要素へ', onOpen: () => select({ view: 'decisions', file: path, element: elementId }) };
  }
  return { label: 'ファイルへ', onOpen: () => select({ view: 'code', file: path, element: undefined }) };
}
